import { Fragment } from 'react';
import { json, redirect } from '@remix-run/node';
import { useLoaderData, useLocation } from '@remix-run/react';
import { ClientOnly } from 'remix-utils/client-only';
import get from 'lodash.get';
import videojscss from 'video.js/dist/video-js.css?url';
import type { MetaFunction } from '@remix-run/node';
import type {
  LinksFunction,
  LoaderFunctionArgs,
} from '@remix-run/server-runtime';
import PlayerError from '~/components/player-error.tsx';
import {
  getAdTagUrl,
  getVmapXml,
} from '~/components/player/advertisement/index.ts';
import { useIsNavigatingBack } from '~/components/player/hooks/use-Is-navigating-back.ts';
import playercss from '~/styles/player.css?url';
import { getConfig } from '~/services/config/index.ts';
import { getContextKeys } from '~/services/pipes/get-context-keys.ts';
import { getAnalyticsSharedVars } from '~/services/analytics/shared-vars.server.ts';
import { getFeedsOrRedirectPath } from '~/services/hooks/index.ts';
import {
  getIsAuthInfoInCookie,
  sessionCookie,
} from '~/services/session/session.server.ts';
import {
  addWebLinks,
  getScreenById,
  getFallbackPlayerScreen,
} from '~/services/router/index.server.ts';
import { getTranslations } from '~/services/localization/index.ts';
import { sessionAuthKey } from '~/services/login/session-auth-key.ts';
import googleImaManifest from '~/services/plugins/zapp_google_interactive_media_ads/config/manifest.ts';
import webPocManifest from '~/services/plugins/web-poc/config/manifest.ts';
import { getServerIsLoggedIn } from '~/services/login/index.ts';
import { getBaseURL } from '~/utils/get-base-url.server.ts';
import { getPluginConfig } from '~/utils/get-plugin-configuration.ts';
import { isValidUrl } from '~/utils/validations.ts';
import {
  getYouboraAnalyticsPluginData,
  getJWAnalyticsPluginData,
  getPlayerPluginConfig,
  getPlayerComponent,
  getMuxAnalyticsPluginData,
  getBrazeAnalyticsPluginData,
  getBrightcoveAnalyticsPluginData,
  getPlayableItemByEntryIdOrIndex,
} from '~/utils/player.ts';
import { cssVarsMetaFunctionData } from '~/services/css-vars/index.ts';
import { getLayoutIdOverride } from '~/services/debug/index.server.ts';
import { getHashForCSS } from '~/utils/hash.ts';
import { useSetIsLoggedInFromServerLoader } from '~/hooks/use-is-logged-in';
import { getSupportedLanguages, getURLLanguage } from '~/utils/i18n.ts';
import type { Feed, FeedEntry } from '~/services/layout/index.ts';
import type { GetContextKeys } from '~/services/pipes/get-context-keys.ts';
import { type VideoAds } from '~/components/player/advertisement/index.ts';
import { type DataSource } from '~/services/layout/types.ts';
import { CACHE_CONTROL_NO_STORE } from '~/services/response';
import continueWatchingManifest from '~/services/plugins/continue-watching/config/manifest.ts';
import pianoAnalyticsManifest from '~/services/plugins/piano-analytics/config/manifest.ts';
import {
  createRequest,
  type CreateRequest,
} from '~/services/pipes/create-request';
import { useFavicon } from '~/hooks/use-favicon';
import { getThemeVars } from '~/services/css-vars/get-theme-vars';

export const links: LinksFunction = () => {
  return [
    { rel: 'stylesheet', href: videojscss },
    { rel: 'stylesheet', href: playercss },
  ];
};

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  return [...cssVarsMetaFunctionData(data)];
};

export async function loader({ request }: LoaderFunctionArgs) {
  const url: URL = new URL(request.url);

  const appName = process.env?.app_name;
  const appVersion = `${process.env?.APP_VERSION_NAME} (${process.env?.APP_VERSION_UUID}) `;
  const layoutIdOverride = getLayoutIdOverride(url);

  const { endpoints, layout, cellStyles, pluginConfigurations } =
    await getConfig();

  const { screens, id: layoutId, content_types: contentTypes } = layout;

  const analyticsSharedVars = await getAnalyticsSharedVars(layoutId);

  const contextKeys: GetContextKeys = await getContextKeys({
    request,
    pluginConfigurations,
  });

  const { account_code, cdn, userId } = getYouboraAnalyticsPluginData(
    pluginConfigurations,
    contextKeys
  );

  const { jw_token, jwUserId } = getJWAnalyticsPluginData(
    pluginConfigurations,
    contextKeys
  );

  const { muxUserId, muxAnalytics } = getMuxAnalyticsPluginData(
    pluginConfigurations,
    contextKeys
  );

  const { brazeUserId, brazeAnalytics } = getBrazeAnalyticsPluginData(
    pluginConfigurations,
    contextKeys
  );

  const { customConfiguration: cw } = getPluginConfig<
    typeof continueWatchingManifest
  >(continueWatchingManifest, pluginConfigurations);

  const { customConfiguration: pianoAnalytics } = getPluginConfig<
    typeof pianoAnalyticsManifest
  >(pianoAnalyticsManifest, pluginConfigurations);

  const continueWatchingSyncedFeed = cw?.synced_feed;

  const continueWatchingUserIdentifier: string | undefined = get(
    contextKeys,
    cw?.user_identifier_storage_key || 'quick-brick-login-flow.access_token'
  );

  const continueWatchingRemoteEventsRequest: CreateRequest =
    await createRequest({
      request,
      endpoints,
      path: cw?.server_sync_feed?.source,
      pluginConfigurations,
    });

  const { session, commitSession } = await sessionCookie(
    request,
    pluginConfigurations
  );

  const searchParams = url.searchParams;
  const queryParams: any = {};
  for (const [key, value] of searchParams) {
    queryParams[key] = value;
  }

  const queryScreenId = queryParams?.['screen-id'];
  const playerScreen =
    getScreenById(screens, queryScreenId) || getFallbackPlayerScreen(screens);
  const currentScreenId = queryScreenId || playerScreen?.id;

  const { brightcove_account_id, brightcoveUserId, brightcoveAnalytics } =
    getBrightcoveAnalyticsPluginData(pluginConfigurations, contextKeys);

  const pluginsData = {
    youboraAccountCode: account_code,
    userId,
    jw_token,
    jwUserId,
    analyticsMapper: { cdn },
    continueWatching: { customConfiguration: cw },
    muxUserId,
    brazeUserId,
    brightcove_account_id,
    brightcoveUserId,
    brightcoveAnalytics,
    pianoAnalytics,
  };

  const screenType = playerScreen?.type;

  const isMock = process.env?.MOCK ? true : false;

  const isContinueWatchingSyncedFeed: boolean =
    isMock || isValidUrl(continueWatchingSyncedFeed?.source);

  const computedUrls = [
    {
      name: 'playableItem',
      path: queryParams?.['self-link'] || queryParams?.['feed-url'],
    },
  ];

  if (isContinueWatchingSyncedFeed) {
    computedUrls.push({ name: 'cw', path: continueWatchingSyncedFeed?.source });
  }

  const { feeds, redirectTo, authInfo } = await getFeedsOrRedirectPath({
    session,
    requestUrl: request.url,
    endpoints,
    screens,
    pluginConfigurations,
    request, //TODO: remove this and use session instead
    currentScreenId,
    computedUrls,
  });

  const headers: Record<string, string> = {
    'Cache-Control': CACHE_CONTROL_NO_STORE,
  };

  const isAuthInfoInCookie = getIsAuthInfoInCookie(pluginConfigurations);

  if (isAuthInfoInCookie) headers['Set-Cookie'] = await commitSession();

  if (redirectTo) {
    if (!isAuthInfoInCookie) await commitSession();

    return redirect(redirectTo, {
      headers,
    });
  }

  session.set(sessionAuthKey, authInfo);

  const isLoggedIn: boolean = getServerIsLoggedIn(authInfo);

  const entryId = queryParams?.['entry-id'];
  const entryIndex = queryParams?.['entry-index'] || 0;

  const playableItemFeed: Feed = feeds?.playableItem?.feed;

  const playableItem = getPlayableItemByEntryIdOrIndex({
    feed: playableItemFeed,
    entryId,
    entryIndex,
  });

  const videoId = playableItem?.extensions?.brightcove?.video_id;

  const continueWatchingInfo = feeds?.cw?.feed?.entry?.find(
    (item: any) => item?.id === get(playableItem, 'id')
  );

  const resumeTime = get(continueWatchingInfo, 'extensions.resumeTime');

  const localizations = playerScreen?.localizations;

  const { customConfiguration: googleImaPlugin } = getPluginConfig<
    typeof googleImaManifest
  >(googleImaManifest, pluginConfigurations);

  const videoAds: VideoAds = playableItem?.extensions?.video_ads;

  const vmapXml: string = await getVmapXml({
    videoAds,
    googleImaPlugin,
  });

  const adTagUrl: string = getAdTagUrl({ videoAds, googleImaPlugin });

  const { customConfiguration: webPoc } = getPluginConfig<
    typeof webPocManifest
  >(webPocManifest, pluginConfigurations);

  const trailerSource: DataSource = webPoc?.trailerSource;

  const urlLanguage = getURLLanguage(url.pathname);

  const feedWithWebLink = addWebLinks({
    feed: playableItemFeed,
    contentTypes,
    screens,
    trailerSource,
    language: urlLanguage,
    supportedLanguages: getSupportedLanguages(),
  });

  const themeVars = getThemeVars(pluginConfigurations);

  const hash = getHashForCSS({
    layout,
    cellStyles,
    themeVars,
  });

  const feedEntryWithWebLink = getPlayableItemByEntryIdOrIndex({
    entryId,
    entryIndex,
    feed: feedWithWebLink,
  });

  const currentPlayableItem: FeedEntry | undefined =
    feedEntryWithWebLink || playableItem;

  if (resumeTime && currentPlayableItem?.extensions) {
    currentPlayableItem.extensions.resumeTime = resumeTime;
  }

  const {
    styles,
    brightcovePlayerAccountId,
    brightcovePlayerPolicyKey,
    brightcovePlayerViewerIdKey,
  } = getPlayerPluginConfig({
    playerScreen,
    pluginConfigurations,
  });

  const brightcovePlayerViewerId = get(
    contextKeys,
    brightcovePlayerViewerIdKey
  );

  const baseURL = getBaseURL();

  if (!isAuthInfoInCookie) await commitSession();

  const isBrightcovePlayer = screenType === 'quick-brick-player-brightcove';

  const playNextUrl = currentPlayableItem?.extensions?.play_next_feed_url;

  return json(
    {
      playableItem: currentPlayableItem,
      videoId: isBrightcovePlayer ? videoId : undefined,
      appData: {
        appName,
        appVersion,
      },
      pluginsData,
      playNextUrl,
      analyticsSharedVars,
      isMock,
      styles,
      brightcovePlayerAccountId,
      brightcovePlayerPolicyKey,
      brightcovePlayerViewerId,
      localizations,
      vmapXml,
      adTagUrl,
      isContinueWatchingSyncedFeed,
      continueWatchingUserIdentifier,
      continueWatchingRemoteEventsRequest,
      isLoggedIn,
      APP_VERSION_UUID: process.env?.APP_VERSION_UUID,
      screen: { id: currentScreenId },
      layoutIdOverride,
      hash,
      muxAnalytics,
      brazeAnalytics,
      brightcoveAnalytics,
      screenType,
      language: urlLanguage,
      feedPlayableItemMessage: !currentPlayableItem
        ? playableItemFeed?.message
        : '',
      baseURL,
    },
    {
      headers,
    }
  );
}

export const playerID = 'vjs_video_3';

export default function () {
  const {
    playableItem,
    videoId,
    appData,
    pluginsData,
    playNextUrl,
    localizations,
    vmapXml,
    screenType,
    feedPlayableItemMessage,
  } = useLoaderData<typeof loader>();

  useFavicon();

  const isNavigatingBack: boolean = useIsNavigatingBack(playerID);

  const t = getTranslations(localizations);

  const location = useLocation();

  useSetIsLoggedInFromServerLoader();

  if (
    !playableItem ||
    (screenType === 'quick-brick-player-brightcove' &&
      !videoId &&
      !playableItem?.content?.src) ||
    (screenType !== 'quick-brick-player-brightcove' &&
      !playableItem?.content?.src)
  ) {
    return (
      <PlayerError message={feedPlayableItemMessage || t?.error_message} />
    );
  }

  const PlayerComponent = getPlayerComponent(screenType);

  return (
    <Fragment key={location.pathname}>
      <script src="//imasdk.googleapis.com/js/sdkloader/ima3.js" defer></script>
      <ClientOnly fallback={<></>}>
        {() => (
          <PlayerComponent
            playableItem={playableItem}
            videoId={videoId}
            pluginsData={pluginsData}
            appData={appData}
            isNavigatingBack={isNavigatingBack}
            playNextUrl={playNextUrl}
            vmapXml={vmapXml}
          />
        )}
      </ClientOnly>
    </Fragment>
  );
}
